// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../Button';
import type { GameSummary } from '../../types/game';

interface GamePreviewProps {
  game: GameSummary;
  isOpen: boolean;
  onClose: () => void;
}

// Mock preview data - in production, this would come from backend
const GAME_PREVIEWS: Record<string, { title: string; description: string; steps: string[] }> = {
  reaction: {
    title: 'Reaction',
    description: 'A party game where players fill in the blanks with the funniest white cards.',
    steps: [
      '1. The Card Czar reads a black card prompt',
      '2. Other players submit their best white cards',
      '3. The Card Czar picks the funniest answer',
      '4. First to 5 points wins!',
    ],
  },
  'terrible-psychiatrist': {
    title: 'Terrible Psychiatrist',
    description:
      'The ultimate diagnosis game where one player is the psychiatrist and others are patients.',
    steps: [
      '1. The psychiatrist reads a patient description',
      '2. All other players submit treatment cards',
      '3. The psychiatrist picks the funniest treatment',
      '4. First to 3 points wins!',
    ],
  },
  'peer-review': {
    title: 'Peer Review',
    description: 'Head-to-head prompt voting battles where creativity meets competition.',
    steps: [
      '1. Receive a creative prompt',
      '2. Submit your witty response',
      '3. Vote on the best submissions',
      '4. Most votes wins the round!',
    ],
  },
  icebreaker: {
    title: 'Icebreaker',
    description: 'Question and answer guessing game to get to know each other better.',
    steps: [
      '1. Answer personal questions',
      "2. Try to guess others' answers",
      '3. Learn surprising facts about friends',
      '4. Points for correct guesses!',
    ],
  },
  'sick-day': {
    title: 'Sick Day',
    description: 'Strategic sick day mechanics with diagnosis and recovery phases.',
    steps: [
      '1. Draw your illness card',
      '2. Choose treatment strategy',
      '3. Manage recovery resources',
      '4. First to recover wins!',
    ],
  },
};

export function GamePreview({ game, isOpen, onClose }: GamePreviewProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const preview = GAME_PREVIEWS[game.game_id] || {
    title: game.display_name,
    description: game.description,
    steps: ['Play to discover how this game works!'],
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3 },
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      transition: { duration: 0.2 },
    },
  };

  const stepVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: { duration: 0.2 },
    },
  };

  const handleNextStep = () => {
    if (currentStep < preview.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="game-preview-backdrop"
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            data-testid="game-preview-modal"
          >
            <motion.div
              className="glass rounded-lg p-8 max-w-2xl w-full border border-purple-500/30"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              data-testid="game-preview-content"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <motion.h2
                  className="font-display text-3xl"
                  data-testid="game-preview-title"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  {preview.title}
                </motion.h2>
                <button
                  onClick={onClose}
                  className="text-2xl text-gray-400 hover:text-gray-200 transition-colors"
                  data-testid="game-preview-close"
                >
                  ✕
                </button>
              </div>

              {/* Description */}
              <motion.p
                className="text-gray-300 mb-6"
                data-testid="game-preview-description"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15 }}
              >
                {preview.description}
              </motion.p>

              {/* Steps */}
              <div className="mb-8">
                <h3
                  className="text-lg font-semibold mb-4 text-purple-300"
                  data-testid="game-preview-steps-title"
                >
                  How to Play
                </h3>

                {/* Step Content */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    className="p-6 bg-white/5 rounded-lg border border-purple-500/20 mb-4"
                    variants={stepVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    data-testid={`game-preview-step-${currentStep}`}
                  >
                    <p className="text-lg text-white" data-testid="game-preview-step-content">
                      {preview.steps[currentStep]}
                    </p>
                  </motion.div>
                </AnimatePresence>

                {/* Step Navigation */}
                <div className="flex items-center justify-between gap-4">
                  <Button
                    onClick={handlePrevStep}
                    disabled={currentStep === 0}
                    variant="secondary"
                    className="text-sm"
                    data-testid="game-preview-prev-button"
                  >
                    ← Previous
                  </Button>

                  {/* Step Indicator */}
                  <div className="flex gap-2">
                    {preview.steps.map((_, idx) => (
                      <motion.button
                        key={idx}
                        onClick={() => setCurrentStep(idx)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          idx === currentStep ? 'bg-purple-400 w-6' : 'bg-white/20'
                        }`}
                        data-testid={`game-preview-step-indicator-${idx}`}
                        whileHover={{ scale: 1.2 }}
                      />
                    ))}
                  </div>

                  <Button
                    onClick={handleNextStep}
                    disabled={currentStep === preview.steps.length - 1}
                    variant="secondary"
                    className="text-sm"
                    data-testid="game-preview-next-button"
                  >
                    Next →
                  </Button>
                </div>
              </div>

              {/* Game Info */}
              <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-white/5 rounded-lg">
                <div data-testid="game-preview-players">
                  <p className="text-xs text-gray-400">Players</p>
                  <p className="text-lg font-semibold text-purple-300">
                    {game.min_players}-{game.max_players}
                  </p>
                </div>
                <div data-testid="game-preview-difficulty">
                  <p className="text-xs text-gray-400">Difficulty</p>
                  <p className="text-lg font-semibold text-purple-300">
                    {game.difficulty || 'Normal'}
                  </p>
                </div>
              </div>

              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                data-testid="game-preview-cta"
              >
                <Button
                  onClick={onClose}
                  variant="success"
                  className="w-full text-sm"
                  data-testid="game-preview-play-button"
                >
                  ▶️ Ready to Play!
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
